import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Box, Checkbox, FormGroup, Stack, styled, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import {
  CaseNote,
  DraftNoteAttachment,
  EconsultProviderTypes,
  EconsultUserRoles,
  FileWithProgress,
  NoteActionTypes,
  NoteStatusCodes,
  NoteTypes
} from '@oh-vcp/components-common';
import { Kpi } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckBoxOptionControl,
  EconsultTextField,
  useNotifierContext
} from '@oh-vcp/components-ui';
import { useQueryClient } from 'react-query';
import EconsultDraftCaseActions from '../econsultDraftCaseActions/EconsultDraftCaseActions';
import EconsultSendDialog from '../econsultSendDialog/EconsultSendDialog';
import CaseService from '../services/caseService';
import { useAuth } from '../auth/AuthContext';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultDragAndDrop, {
  EconsultDragAndDropRef
} from '../econsultDragAndDrop/EconsultDragAndDrop';
import EconsultDraftLostWarningDialog from '../econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';

interface EconsultCaseProvideConsultProps {
  showRecommendation?: boolean;
  noteFieldLabel?: string;
  actionType: (typeof NoteActionTypes)[keyof typeof NoteActionTypes];
  draftNote?: CaseNote;
  setPreviousDraftNote: (prevDraftNote: CaseNote | undefined) => void;
  onNoteContentChanged?: (noteContent: string) => void;
}

export interface EconsultCaseProvideConsultRef {
  saveDraft: () => void;
}

const NoteField = styled(EconsultTextField)<{ isNoteError: boolean }>(({ theme, isNoteError }) =>
  isNoteError
    ? {
        border: `1px solid ${theme.palette.error.main}`,
        borderRadius: 5,
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: theme.palette.error.main
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.error.main,
            boxShadow: 'none'
          }
        }
      }
    : {}
);

const EconsultCaseProvideConsult = forwardRef<
  EconsultCaseProvideConsultRef,
  EconsultCaseProvideConsultProps
>((props, ref) => {
  const {
    showRecommendation,
    noteFieldLabel,
    actionType,
    draftNote,
    setPreviousDraftNote,
    onNoteContentChanged
  } = props;
  const { t } = useTranslation();
  const { providerType, statusType } = useParams();
  const navigate = useNavigate();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { caseDetails, setCaseDetails, userRole, abortAllUploads } =
    useEconsultCaseDetailsContext();
  const { draftCaseSaveDisable, setDraftCaseSaveDisable } = useEconsultCaseDetailsContext();
  const { setDraftCaseSaved, setDraftNote } = useEconsultCaseDetailsContext();
  const [caseAttachments, setCaseAttachments] = useState<DraftNoteAttachment[]>([]);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState<boolean>(false);
  const [isRecommendedToBeSeen, setIsRecommendedToBeSeen] = useState<boolean>(false);
  const [noteContent, setNoteContent] = useState<string>(draftNote?.noteContent || '');
  const [disableActionButtons, setDisableActionButtons] = useState<boolean>(false);
  const [draftFiles, setDraftFiles] = useState<FileWithProgress[]>([]);
  const [isNoteError, setIsNoteError] = useState<boolean>(false);
  const attachFileRef = useRef<EconsultDragAndDropRef>(null);
  const attachmentSectionRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuth();
  const currentDraftNote = useRef<CaseNote | undefined>(undefined);
  const queryClient = useQueryClient();
  const [draftNoteChanged, setDraftNoteChanged] = useState<boolean>(false);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const isInitialized = useRef(false);

  if (!user || !caseDetails) {
    return null;
  }
  const handleBeforeUnload = (event: any) => {
    if (window.location.toString().indexOf('logout') > -1) {
      return;
    }
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome and Firefox
    return ''; // Required for Safari
  };
  const handlePopState = (event: PopStateEvent) => {
    console.log(`popstate: ${event}`);
    if (caseDetails && draftNoteChanged) {
      setIsDraftWarningDialogOpen(true);
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (draftNoteChanged) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [draftNoteChanged]);
  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (draftNote?.attachments && draftNote?.attachments?.length > 0) {
      const draftReplyNoteAttachmentFiles: FileWithProgress[] = draftNote?.attachments.map((f) => {
        return {
          file: {
            name: f.uploadedFile?.fileName
          } as unknown as File,
          progress: 100,
          uploadedFile: f.uploadedFile,
          fileDescription: f.fileDescription,
          fileId: f.fileId,
          uid: crypto.randomUUID()
        };
      });
      if (draftReplyNoteAttachmentFiles && draftReplyNoteAttachmentFiles.length > 0) {
        setDraftFiles(draftReplyNoteAttachmentFiles);
      }

      setCaseAttachments((prevAttachments) => {
        const existingFileIds = new Set(prevAttachments.map((a) => a.fileId));

        const newAttachments =
          draftNote?.attachments
            ?.map((d) => ({
              fileId: d.uploadedFile?.fileId || 0,
              statusCd: NoteStatusCodes.active,
              fileName: d.uploadedFile?.fileName || '',
              ...(d.attachmentId ? { attachmentId: d.attachmentId } : {}),
              ...(d.fileDescription ? { fileDescription: d.fileDescription } : {})
            }))
            .filter((a) => !existingFileIds.has(a.fileId)) || [];

        return [...prevAttachments, ...newAttachments];
      });
    }
  }, [draftNote?.attachments]);

  const getNotificationMessage = () => {
    switch (actionType) {
      case NoteActionTypes.addNote:
        return t('EconsultNoteAdd.success.message');
      case NoteActionTypes.writeConsult:
        return t('EconsultConsultProvided.success.message');
      case NoteActionTypes.reply:
        return t('EconsultMoreInfoProvided.success.message');
      case NoteActionTypes.askForMoreInfo:
        return t('EconsultMoreInfoRequested.success.message');
      case NoteActionTypes.reopen:
        return t('EconsultClarification.success.message');
      default:
        return t('EconsultGeneral.success.message');
    }
  };

  const getCaseDraftNotes = async (caseId: number | undefined) => {
    if (!caseId) {
      return undefined;
    }
    const caseInfo = await CaseService.getCaseDetails(caseId);
    const noteType =
      userRole === EconsultUserRoles.consultant || userRole === EconsultUserRoles.consultantDelegate
        ? NoteTypes.consultant
        : NoteTypes.referrer;
    const draftNotes: CaseNote[] = [];
    if (caseInfo && caseInfo.data && caseInfo.data.notes) {
      caseInfo.data.notes.forEach((note) => {
        if (
          note.statusCd &&
          note.statusCd === NoteStatusCodes.draft &&
          noteType === note.noteTypeCd
        ) {
          draftNotes.push(note);
        }
      });
    }
    return draftNotes;
  };

  const deleteNote = async (note: CaseNote) => {
    await CaseService.upsertCaseNote({
      noteId: note.noteId,
      ownerId: note.ownerId,
      noteTypeCd: note.noteTypeCd,
      actionTypeCd: actionType,
      caseId: note.caseId,
      statusCd: NoteStatusCodes.deleted,
      kpis: undefined,
      noteContent: note.noteContent,
      attachments: note.attachments
    });
  };

  const handleSaved = async (kpi?: Kpi) => {
    setIsSendDialogOpen(false);
    let noteId =
      currentDraftNote.current && currentDraftNote.current.noteId
        ? currentDraftNote.current.noteId
        : undefined;
    setDraftCaseSaveDisable(true);
    setDisableActionButtons(true);
    if (!noteId) {
      const draftNotes = await getCaseDraftNotes(caseDetails.caseId);
      if (draftNotes && draftNotes.length > 0) {
        noteId = draftNotes.pop()?.noteId;
        draftNotes.forEach((draftN) => {
          if (draftN.noteId !== noteId) {
            deleteNote(draftN);
          }
        });
      }
    }
    removeAllErrorNotifications();
    const note = await CaseService.upsertCaseNote({
      noteId,
      ownerId: user.userId as number,
      noteTypeCd:
        userRole === EconsultProviderTypes.consultant.toUpperCase() ||
        userRole === EconsultUserRoles.consultantDelegate
          ? NoteTypes.consultant
          : NoteTypes.referrer,
      actionTypeCd: actionType,
      caseId: caseDetails.caseId,
      statusCd: NoteStatusCodes.active,
      kpis: kpi && actionType === NoteActionTypes.addNote ? [kpi] : undefined,
      noteContent
    });
    setDraftCaseSaved(true);
    setDraftNote(undefined);
    setPreviousDraftNote(undefined);
    if (actionType !== NoteActionTypes.addNote) {
      const updatedCaseDetails = {
        ...caseDetails,
        participant: {
          ...caseDetails?.participant,
          statusCd:
            actionType === NoteActionTypes.reopen
              ? NoteStatusCodes.active
              : caseDetails?.participant?.statusCd,
          statusReason:
            actionType === NoteActionTypes.reopen ? '' : caseDetails?.participant?.statusReason
        },
        statusCd: NoteStatusCodes.active,
        notes: [
          {
            ...note,
            patientToBeSeen: isRecommendedToBeSeen,
            kpis: kpi ? [kpi] : undefined
          }
        ]
      };
      setCaseDetails(updatedCaseDetails);
      await CaseService.upsertCaseDetails(updatedCaseDetails);
    }
    setDraftCaseSaveDisable(false);
    currentDraftNote.current = undefined;
    addNotification({
      type: 'success',
      message: getNotificationMessage()
    });
    setDisableActionButtons(false);
    setDraftNoteChanged(false);
    setTimeout(() => {
      if (providerType === 'search') {
        queryClient.invalidateQueries(['caseSearch']);
      } else {
        queryClient.invalidateQueries(['caseResults']);
      }
    }, 300);
    const urlToNavigate = CaseService.getReturnNavigationUrl(providerType, statusType, user);
    navigate(urlToNavigate.toLowerCase());
  };

  const getCurrentDraftNote = (content: string | undefined) => {
    let noteId =
      currentDraftNote.current && currentDraftNote.current.noteId
        ? currentDraftNote.current.noteId
        : undefined;
    if (!noteId) {
      const curDraftNote = caseDetails?.notes?.find(
        (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === actionType
      );
      noteId = curDraftNote ? curDraftNote.noteId : undefined;
    }
    return {
      ownerId: (user.userId || user.userid) as number,
      noteId,
      noteTypeCd:
        userRole === EconsultProviderTypes.consultant.toUpperCase() ||
        userRole === EconsultUserRoles.consultantDelegate
          ? NoteTypes.consultant
          : NoteTypes.referrer,
      actionTypeCd: actionType,
      caseId: caseDetails.caseId,
      statusCd: NoteStatusCodes.draft,
      kpis: undefined,
      noteContent: content || noteContent,
      attachments: caseAttachments
        ? caseAttachments.map((c) => ({
            fileId: c.fileId,
            statusCd: c.statusCd,
            ...(c.attachmentId ? { attachmentId: c.attachmentId } : {}),
            ...(c.fileDescription ? { fileDescription: c.fileDescription } : {})
          }))
        : []
    };
  };

  const handleDraftSaved = async () => {
    setDraftCaseSaveDisable(true);
    const caseDraftNotes = await getCaseDraftNotes(caseDetails.caseId);
    const noteId =
      caseDraftNotes && caseDraftNotes.length > 0 ? caseDraftNotes[0].noteId : undefined;
    const note = await CaseService.upsertCaseNote({
      ownerId: (user.userId || user.userid) as number,
      noteId,
      noteTypeCd:
        userRole === EconsultProviderTypes.consultant.toUpperCase() ||
        userRole === EconsultUserRoles.consultantDelegate
          ? NoteTypes.consultant
          : NoteTypes.referrer,
      actionTypeCd: actionType,
      caseId: caseDetails.caseId,
      statusCd: NoteStatusCodes.draft,
      kpis: undefined,
      noteContent
    });
    setCaseDetails((prev) => ({
      ...prev,
      notes: (prev?.notes ?? []).some((n) => n.noteId === note.noteId)
        ? (prev?.notes ?? []).map((n) => (n.noteId === note.noteId ? note : n))
        : [...(prev?.notes ?? []), note]
    }));
    setDraftCaseSaveDisable(false);
    setDraftCaseSaved(true);
    setDraftNoteChanged(false);
    setPreviousDraftNote(JSON.parse(JSON.stringify(note)));
    currentDraftNote.current = note;
    setIsSendDialogOpen(false);
    return note;
  };

  useImperativeHandle(ref, () => ({
    saveDraft() {
      handleDraftSaved();
    }
  }));

  const handleSend = () => {
    removeAllErrorNotifications();
    const attachments = attachFileRef.current?.getDraftAttachments();
    if (
      (actionType === NoteActionTypes.writeConsult ||
        actionType === NoteActionTypes.writeConsultDraft) &&
      !noteContent
    ) {
      addNotification({
        type: 'error',
        message: t('Case.error.noteSend')
      });
      setIsNoteError(true);
      return;
    }
    if (!noteContent && (!attachments || attachments.length === 0)) {
      addNotification({
        type: 'error',
        message: t('Case.error.noteSend2')
      });
      setIsNoteError(true);
      return;
    }
    setIsNoteError(false);
    if (
      userRole === EconsultProviderTypes.consultant.toUpperCase() ||
      userRole === EconsultUserRoles.consultantDelegate
    ) {
      setIsSendDialogOpen(true);
    } else {
      handleSaved(undefined);
    }
  };
  const onDraftSave = () => {
    removeAllErrorNotifications();
    handleDraftSaved();
    addNotification({
      type: 'success',
      message: t('Case.consult.draftSaved')
    });
  };
  const handleCancel = () => {
    setIsSendDialogOpen(false);
  };

  const getDraftCaseSaveDisable = () => {
    return draftCaseSaveDisable;
  };
  const handleNoteContentChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNoteContent(event.target.value);
    setDraftCaseSaved(false);
    setDraftNote(getCurrentDraftNote(event.target.value));
    setIsNoteError(false);
    setDraftNoteChanged(true);
    if (onNoteContentChanged) {
      onNoteContentChanged(event.target.value);
    }
  };
  const handleFileAttach = () => {
    if (attachFileRef && attachFileRef.current) {
      attachFileRef.current.attachFile();
      setDraftCaseSaved(false);
      setDraftNote(getCurrentDraftNote(undefined));
      setDraftNoteChanged(true);
    }
  };
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setDraftNote(undefined);
    setCaseDetails(undefined);
    abortAllUploads();
    navigate(-1); // Allow navigation back
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
    window.history.pushState(null, '', window.location.href);
  };

  const scrollToAttachmentSection = () => {
    if (attachmentSectionRef && attachmentSectionRef.current) {
      attachmentSectionRef.current.scrollIntoView();
    }
  };

  const handleOnStartAttachment = () => {
    setDisableActionButtons(true);
    scrollToAttachmentSection();
  };

  const handleOnCompleteFileUpload = () => {
    setDisableActionButtons(false);
  };

  useEffect(() => {
    if (!isInitialized.current && caseDetails) {
      handleDraftSaved();
      isInitialized.current = true;
    }
  }, [caseDetails]);

  const handleOnFileDeleted = async () => {
    if (!caseDetails?.caseId) return;
    queryClient.invalidateQueries(['caseDetails', caseDetails?.caseId]);
  };

  return (
    <>
      <Stack direction="column">
        <NoteField
          isNoteError={isNoteError}
          value={noteContent}
          onChange={handleNoteContentChanged}
          multiline
          rows={6}
          fullWidth
          placeholder={`${noteFieldLabel || (t('Case.consult.enterNote') as string)}...`}
          inputProps={{
            style: {
              resize: 'vertical',
              overflow: 'auto'
            }
          }}
        />
        {isNoteError && (
          <Typography variant="body2" color="error.main" mt={1}>
            {t('Case.consult.requiredField')}
          </Typography>
        )}
        {showRecommendation && (
          <FormGroup
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setIsRecommendedToBeSeen(event.target.checked)
            }>
            <Box display="flex" alignItems="center">
              <CheckBoxOptionControl
                sx={{ mr: 0 }}
                control={<Checkbox checked={isRecommendedToBeSeen} />}
                label=""
                disableTypography
              />
              <Box component="span" sx={{ ml: 0 }}>
                <Typography variant="body4">{t('Case.consult.recommendation')}</Typography>
              </Box>
            </Box>
          </FormGroup>
        )}
        <Box mt={3}>
          <EconsultDraftCaseActions
            onSaveDraft={onDraftSave}
            onSend={handleSend}
            onAttach={handleFileAttach}
            getSaveDraftDisabled={getDraftCaseSaveDisable}
            disableActionButtons={disableActionButtons}
          />
        </Box>
        <Box mt={3} mb={3} ref={attachmentSectionRef}>
          <EconsultDragAndDrop
            ref={attachFileRef}
            draftUploadedFiles={draftFiles}
            onLastNoteNotExists={handleDraftSaved}
            onStartFileUpload={handleOnStartAttachment}
            onCompleteFileUpload={handleOnCompleteFileUpload}
            onFileDeleted={handleOnFileDeleted}
            allowDelete
          />
        </Box>
      </Stack>
      <EconsultSendDialog
        isOpen={isSendDialogOpen}
        onCancel={handleCancel}
        onConfirm={handleSaved}
      />
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
    </>
  );
});

export default EconsultCaseProvideConsult;
